import React, { useState, useEffect, useRef } from 'react';
import { Bell } from 'lucide-react';
import api from '../services/api';
import { getSocket } from '../services/socket';

export default function NotificationBell() {
  const [notes, setNotes]   = useState([]);
  const [open, setOpen]     = useState(false);
  const ref = useRef(null);
  const unread = notes.filter(n => !n.read).length;

  useEffect(() => {
    api.get('/notifications').then(r => setNotes(r.data)).catch(() => {});
    const sock = getSocket();
    if (sock) {
      const handler = (note) => setNotes(prev => [note, ...prev]);
      sock.on('notification', handler);
      return () => sock.off('notification', handler);
    }
  }, []);

  useEffect(() => {
    function onClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  async function markRead(id) {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotes(prev => prev.map(n => n._id === id ? { ...n, read: true } : n));
    } catch (_) { /* transient — stays unread, next click retries */ }
  }

  return (
    <div ref={ref} style={{position:'relative'}}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          background: open ? 'var(--bg-surface-3)' : 'var(--bg-surface-2)',
          border: '1px solid var(--border-subtle)',
          width: 36, height: 36, borderRadius: 8,
          display:'inline-flex', alignItems:'center', justifyContent:'center',
          color: 'var(--text-primary)', cursor: 'pointer', position:'relative',
        }}
        aria-label="Notifications"
      >
        <Bell size={16} />
        {unread > 0 && (
          <span style={{
            position: 'absolute', top: -4, right: -4,
            background:'var(--loss)', color:'#fff',
            fontSize: 10, fontWeight: 700, lineHeight: 1,
            padding: '3px 5px', borderRadius: 999, minWidth: 16, textAlign:'center',
          }}>
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div style={{
          position:'absolute', top:'calc(100% + 8px)', right:0, zIndex:1000,
          minWidth:340, maxHeight:440, overflowY:'auto',
          background:'var(--bg-surface)',
          border:'1px solid var(--border-strong)',
          borderRadius:'var(--radius-md)',
          boxShadow:'var(--shadow-modal)',
        }}>
          <div style={{
            padding:'12px 16px',
            borderBottom:'1px solid var(--border-subtle)',
            fontWeight:600, fontSize:13,
          }}>Notifications</div>
          {notes.length === 0 && (
            <div style={{padding:'14px 16px', color:'var(--text-tertiary)', fontSize:12.5}}>
              No notifications
            </div>
          )}
          {notes.slice(0, 20).map(n => (
            <div key={n._id}
              style={{
                padding:'12px 16px',
                borderBottom:'1px solid var(--border-subtle)',
                cursor:'pointer',
                background: !n.read ? 'var(--bg-surface-2)' : 'transparent',
              }}
              onClick={() => markRead(n._id)}
            >
              <div style={{display:'flex',justifyContent:'space-between',gap:8}}>
                <strong style={{fontSize:12.5,color:'var(--text-primary)'}}>{n.title}</strong>
                <span style={{fontSize:11,color:'var(--text-tertiary)'}}>
                  {new Date(n.createdAt).toLocaleTimeString()}
                </span>
              </div>
              <p style={{margin:'4px 0 0',fontSize:12,color:'var(--text-secondary)'}}>{n.message}</p>
              {!n.read && (
                <span style={{
                  display:'inline-block', marginTop:6,
                  fontSize:9, fontWeight:600, padding:'2px 6px', borderRadius:4,
                  background:'rgba(59,130,246,0.15)', color:'var(--accent)',
                  textTransform:'uppercase', letterSpacing:'0.06em',
                }}>NEW</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
